import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import LoginImg from "../../../assets/loginImg.png";
import JusttawkLogo from "../../../assets/loginImg2.png";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";
import GoogleLoginButton from "./GoogleLoginButton";

const centeredFlex = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ipAddress, setIpAddress] = useState("");
  const [consentChecked, setConsentChecked] = useState(true);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    addIpAddress();
  }, []);

  const addIpAddress = async () => {
    try {
      const res = await axios.get("https://api64.ipify.org/?format=json");
      setIpAddress(res.data.ip);
    } catch (e) {
      console.error("IP fetch failed:", e);
    }
  };

  const getQueryParam = (key) => {
    const params = new URLSearchParams(location.search);
    return params.get(key);
  };

  const getAllUtmParams = () => {
    return {
      utm_source: getQueryParam("utm_source") || "",
      utm_medium: getQueryParam("utm_medium") || "",
      utm_campaign: getQueryParam("utm_campaign") || "",
      utm_content: getQueryParam("utm_content") || "",
    };
  };

  const registerUser = async (value) => {
    const utmParams = getAllUtmParams();

    const data = {
      email: value,
      ip_address: ipAddress,
      country: "",
      dropoff: "login",
      loginMethod: "email-otp",
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
    };

    try {
      setIsSubmitting(true);

      const response = await axiosInstance.post(
        apiEndPointsConfig.addLogin,
        data
      );

      localStorage.setItem("isNew", response?.data?.isNew);
      localStorage.setItem("emailNo", value);
      localStorage.setItem("userId", response?.data?.userId);

      if (response?.data?.success === true) {
        navigate("/otp");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .email("Must be a valid email address"),
    }),
    onSubmit: async (values) => {
      if (!consentChecked) {
        setConsentError(true);
        return;
      }
      const lowercaseEmail = values.email.toLowerCase();
      await registerUser(lowercaseEmail);
    },
  });

  const handleGoogleLogin = () => {
    if (!consentChecked) {
      setConsentError(true);
      return;
    }
    console.log("Proceed with Google login...");
  };

  const disableActions = !consentChecked || isSubmitting;

  return (
    <Box>
      <Grid
        container
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
        }}
      >
        {/* Left Section */}
        <Grid item xs={12} md={6.5}>
          <Box
            sx={{
              display: "flex",
              minHeight: "100vh",
              position: "relative",
              mt: { xs: "-150px", md: 0 },
            }}
          >
            <Box sx={{ display: "flex", width: "100%" }}>
              <Box
                sx={{
                  display: { xs: "none", md: "initial" },
                  width: "90%",
                  bgcolor: "#243e93",
                }}
              />
              <Box
                sx={{
                  display: { xs: "none", md: "initial" },
                  width: "10%",
                  bgcolor: "#3e55a0",
                }}
              />
            </Box>

            {/* Form Container */}
            <Container maxWidth="sm" sx={{ ...centeredFlex, p: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 0, md: 5 },
                  borderRadius: 3,
                  width: "100%",
                  maxWidth: { xs: "90%", md: 500 },
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  border: "1px solid #dedede",
                  ml: { xs: 0, md: 5 },
                }}
              >
                <form onSubmit={formik.handleSubmit}>
                  <Box sx={{ p: { xs: "25px 25px 5px 25px", md: 0 } }}>
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                      <Typography variant="h4" fontWeight="bold">
                        Hey! Good Choice.
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ mt: 0.5, fontWeight: 500 }}
                      >
                        It's free. No credit card needed.
                      </Typography>
                    </Box>

                    <Typography>Please enter your email address*</Typography>
                    <TextField
                      id="email"
                      name="email"
                      variant="outlined"
                      margin="normal"
                      placeholder="Email address"
                      fullWidth
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isSubmitting}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />

                    {/* Consent */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mt: 1,
                        p: "10px 0",
                      }}
                    >
                      <Checkbox
                        checked={consentChecked}
                        disabled={isSubmitting}
                        onChange={(e) => {
                          setConsentChecked(e.target.checked);
                          setConsentError(false);
                        }}
                      />
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        By submitting your details, you consent to receive
                        calls, emails, and messages from Justttawk on the
                        provided contact information. Your data is safe and will
                        be kept confidential.
                      </Typography>
                    </Box>

                    {consentError && (
                      <Typography
                        sx={{ color: "red", fontSize: "0.8rem", mt: 0.5 }}
                      >
                        You must accept the communication consent to proceed.
                      </Typography>
                    )}

                    {/* Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      type="submit"
                      size="large"
                      disabled={disableActions}
                      sx={{
                        mt: 2,
                        bgcolor: "#243e93",
                        "&:hover": { bgcolor: "#152a60" },
                        py: 1.5,
                        textTransform: "capitalize",
                        borderRadius: "8px",
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Please wait...
                        </>
                      ) : (
                        "Get Started"
                      )}
                    </Button>
                  </Box>
                </form>

                <Box>
                  <Box sx={{ px: { xs: 0, md: 2 }, py: 2 }}>
                    <Divider>
                      <Chip label="Or" size="small" />
                    </Divider>
                  </Box>

                  <Box sx={{ px: { xs: 2, md: 0 }, pb: { xs: 2, md: 0 } }}>
                    <GoogleLoginButton
                      onClick={handleGoogleLogin}
                      disabled={disableActions}
                      loading={isSubmitting}
                    />
                  </Box>
                </Box>
              </Paper>
            </Container>
          </Box>
        </Grid>

        {/* Right Section */}
        <Grid item xs={12} md={5.5}>
          <Box
            sx={{
              minHeight: "100%",
              p: 3,
              bgcolor: { xs: "#243E93E0", md: "white" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <img
                src={JusttawkLogo}
                alt="Justtawk Logo"
                style={{ maxWidth: 210 }}
              />
            </Box>

            <Grid container>
              <Grid item md={10} xs={12}>
                <Box
                  sx={{
                    ...centeredFlex,
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "100%", md: 400 },
                      display: { xs: "none", md: "initial" },
                    }}
                  >
                    <img
                      style={{ width: "100%" }}
                      src={LoginImg}
                      alt="Login Illustration"
                    />
                  </Box>
                </Box>

                <Box sx={{ ...centeredFlex }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      textAlign: "center",
                      pb: { xs: "100px", md: "0" },
                    }}
                  >
                    <Typography
                      sx={{
                        color: { xs: "white", md: "black" },
                        fontSize: { xs: "1.5rem", md: "2.15rem" },
                        fontWeight: 900,
                      }}
                    >
                      Welcome to Justtawk
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 500,
                        lineHeight: "25px",
                        mt: 1,
                        color: { xs: "white", md: "black" },
                        fontSize: { xs: "1rem", md: "1.2rem" },
                      }}
                    >
                      Affordable call center solution that helps your business
                      by seamlessly receiving and managing customer calls.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
